import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Button } from '../ui/button'
import { ChevronDown, ChevronUp, Menu, X } from 'lucide-react'
import logo from '../../assets/logo.png'
import { Link, useLocation, useNavigate } from 'react-router'
import MobileNavMenu from './MobileMenu'

interface HeaderProps {
  navItems: {
    path: string
    label: string
    children?: { path: string; label: string }[]
  }[]
  isDemo?: boolean
}

export function Header1({ navItems, isDemo = false }: HeaderProps) {
  const location = useLocation()
  const navigate = useNavigate()

  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [activeParent, setActiveParent] = useState<string | null>(null)
  const [showHeader, setShowHeader] = useState(true)
  const [lastScrollY, setLastScrollY] = useState(0)

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY

      if (currentScrollY < lastScrollY || currentScrollY <= 50) {
        setShowHeader(true)
      } else {
        setShowHeader(false)
      }

      setLastScrollY(currentScrollY)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [lastScrollY])

  const scrollToSection = (path: string) => {
    navigate(path)
    setIsMenuOpen(false)
    window.scrollTo(0, 0)
  }

  const handleMouseEnter = (parent: string) => {
    setActiveParent(parent)
  }

  const handleMouseLeave = () => {
    setActiveParent(null)
  }

  return (
    <motion.header
      className={`top-0 left-0 w-full transition-all duration-300 z-50 ${!isDemo ? 'fixed' : ''}  ${showHeader ? 'translate-y-0 opacity-100 ' : ''} bg-white/95 backdrop-blur-md shadow-lg border-b border-gray-300 border-border`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ">
        <div className="flex justify-between items-center h-20">
          {/* 🖼️ Logo */}
          <Link to={'/'}>
            <motion.div
              className="flex items-center space-x-3"
              whileHover={{ scale: 1.05 }}
              transition={{ type: 'spring', stiffness: 300 }}
            >
              <img className="h-10" src={logo} alt="logo" />
            </motion.div>
          </Link>

          {/* 🧭 Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {navItems.map((item, index) => {
              const isActive =
                location?.pathname === item.path ||
                item.children?.some((child) => location.pathname === child.path)

              return (
                <div
                  key={item.label}
                  className="relative group"
                  onMouseEnter={() => handleMouseEnter(item.label)}
                  onMouseLeave={handleMouseLeave}
                >
                  {item.children ? (
                    <>
                      {/* Parent Link with Dropdown */}
                      <motion.div
                        className={`font-medium cursor-pointer relative transition-colors flex items-center ${isActive
                          ? 'text-green-600 font-semibold'
                          : 'text-gray-700 hover:text-green-600'
                          }`}
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                      >
                        {item.label}
                        {activeParent === item.label || isActive ? (
                          <ChevronUp className="w-4 h-4 mr-2" />
                        ) : (
                          <ChevronDown className="w-4 h-4 mr-2" />
                        )}
                      </motion.div>

                      {/* Dropdown Items */}
                      <div
                        className={`absolute hidden group-hover:block top-full pt-5 z-50 ${activeParent === item.label ? 'block' : 'hidden'
                          }`}
                      >
                        <div className="w-48 bg-white rounded-md shadow-lg py-2 z-10 transition-all duration-300 origin-top scale-y-0 group-hover:scale-y-100 border-[1px] border-gray-200 overflow-hidden">
                          {item.children.map((child) => (
                            <motion.button
                              key={child.label}
                              onClick={() => scrollToSection(child.path)}
                              className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100 hover:text-green-600 transition-colors"
                              whileHover={{ x: 5 }}
                              transition={{ type: 'spring', stiffness: 500 }}
                            >
                              {child.label}
                            </motion.button>
                          ))}
                        </div>
                      </div>
                    </>
                  ) : (
                    // Single link
                    <motion.button
                      onClick={() => scrollToSection(item.path)}
                      className={`relative font-medium transition-colors ${isActive
                        ? 'text-green-600 font-semibold'
                        : 'text-gray-700 hover:text-green-600'
                        }`}
                      initial={{ opacity: 0, y: -20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      {item.label}
                    </motion.button>
                  )}
                </div>
              )
            })}
          </nav>

          {/* 📱 Mobile Menu / CTA */}
          <div className="flex items-center space-x-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
            >
              <Button
                onClick={() => {
                  navigate('/contact')
                }}
                className="hidden md:flex bg-green-600 hover:bg-green-700 text-white px-6 py-2"
              >
                Get Free Quote
              </Button>
            </motion.div>

            {/* 🍔 Mobile Menu Toggle */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              {isMenuOpen ? (
                <X className="w-5 h-5" />
              ) : (
                <Menu className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* 📱 Mobile Nav */}
      <div>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden border-t border-gray-200 bg-white"
          >
            <div className="px-4 py-6 space-y-4">

              <MobileNavMenu navItems={navItems} scrollToSection={scrollToSection} />

            </div>
          </motion.div>
        )}
      </div>
    </motion.header>
  )
}
